/**
 * Sign-In Dialog — modal chrome wrapper around <SignInForm />.
 *
 * The form itself (OAuth buttons, password forms, reset, account-not-found
 * branch) lives in SignInForm so it can be reused inline (e.g. inside the
 * TableScanInterstitial) without a modal. This component contributes only
 * the dialog chrome (backdrop, animated card, close button, logo) and bridges
 * SignInDialogContext's view + open/close state into the shared form.
 */

import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import logo from "../assets/icons/outclose.svg";
import { useSignInDialog } from "../lib/platform/SignInDialogContext";
import { useT } from "../lib/i18n";
import SignInForm, { type SignInFormView } from "./SignInForm";

export function SignInDialog() {
  const { t } = useT();
  const { isOpen, view, options, close, forceClose, setView } = useSignInDialog();

  const asSignUp = options.asSignUp ?? false;
  const preventClose = options.preventClose ?? false;

  const handleClose = () => {
    close();
  };

  const handleSuccess = () => {
    options.onSuccess?.();
    forceClose();
  };

  const handleAccountNotFoundCreate = () => {
    // Stays signed in; ProtectedRoute will redirect to /quiz when profile has
    // no quizCompletedAt.
    forceClose();
  };

  // View-aware default title/subtitle so the dialog feels right when the
  // caller doesn't override them.
  const defaultTitle = (() => {
    if (options.title) return options.title;
    switch (view as SignInFormView) {
      case "signup-password": return t("auth.signUp.title");
      case "signin-password": return t("auth.signIn.title");
      case "reset-password": return t("auth.forgotPassword.title");
      case "account-not-found": return t("auth.accountNotFound.title");
      case "email-code-verify": return t("auth.emailCode.title");
      case "email-code-name": return t("auth.emailCode.nameTitle");
      default: return t("auth.dialog.welcomeTitle");
    }
  })();

  const defaultSubtitle = (() => {
    if (options.subtitle) return options.subtitle;
    switch (view as SignInFormView) {
      case "signup-password": return t("auth.signUp.subtitle");
      case "signin-password": return t("auth.signIn.subtitle");
      case "reset-password": return t("auth.forgotPassword.subtitle");
      case "account-not-found": return t("auth.accountNotFound.message");
      // Both steps render their own contextual line (the target address /
      // why we're asking), so the dialog stays quiet above them.
      case "email-code-verify":
      case "email-code-name":
        return "";
      default:
        return asSignUp
          ? t("auth.dialog.signUpSubtitle")
          : t("auth.dialog.signInSubtitle");
    }
  })();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // z-[9999] is the app's modal layer — SheetModal, CancellationModal,
          // BuddyDetailModal and ProfileImageEditor all sit there. This dialog
          // was left at z-50 from when nothing full-screen outranked it, and
          // the onboarding route's own full-bleed layer (z-[60]) then covered
          // it: tapping "Log in" during signup opened a dialog behind the flow.
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
          onClick={preventClose ? undefined : handleClose}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-neutral-50/95 dark:bg-neutral-900/95 rounded-2xl shadow-2xl max-w-md w-full max-h-[95vh] overflow-y-auto my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {!preventClose && (
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors"
              >
                <X className="w-5 h-5 dark:text-neutral-300 text-neutral-800" />
              </button>
            )}

            <div className="p-6 pb-4 text-center">
              <div className="flex items-center justify-center mb-4">
                <img
                  src={logo}
                  alt="Outclose Logo"
                  className="w-24 h-24 dark:invert"
                />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2">
                {defaultTitle}
              </h2>
              <p className="text-primary-400 text-sm">{defaultSubtitle}</p>
            </div>

            <div className="px-6 pb-6">
              <SignInForm
                view={view as SignInFormView}
                onViewChange={setView}
                asSignUp={asSignUp}
                onSuccess={handleSuccess}
                onAccountNotFoundCreate={handleAccountNotFoundCreate}
                // A redirect from here comes back to a page with no dialog on
                // it, so `handleSuccess` above is unreachable and AuthProvider
                // finishes the job from these two instead.
                redirectSurface="dialog"
                redirectReturnTo={options.returnTo}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SignInDialog;
